import DummyLogo from "@/assets/images/lucidity-placeholder.png";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { getPodcastExpertBySlug } from "@/services/api/Series";
import secondsToMinutes from "@/utils/secondsToMinutes";
import { ArrowRight, Play, RocketLaunch } from "@phosphor-icons/react";
import Image from "next/legacy/image";
import Link from "next/link";
import { useEffect, useState } from "react";

/**
 * Episode list for a featured expert, shown without leaving the page.
 *
 * The grid only knows each expert's summary, so the episodes are fetched when
 * the modal opens rather than shipped with the index page — with a handful of
 * experts that keeps the initial payload small, and nothing is requested for
 * cards nobody clicks. The full expert page stays the canonical, shareable
 * version and is linked at the bottom.
 */
export default function ExpertEpisodesModal({ expert, isOpen, onOpenChange }) {
  const [detail, setDetail] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!isOpen || !expert?.slug) return;

    // Track the slug this request was made for: a viewer can close one modal
    // and open another before the first response lands, and applying a stale
    // response would show the wrong person's episodes.
    let isCurrent = true;

    const fetchDetail = async () => {
      setIsLoading(true);
      setHasError(false);

      try {
        const response = await getPodcastExpertBySlug(expert.slug);
        if (isCurrent) {
          setDetail(response?.data?.data || null);
        }
      } catch (error) {
        if (isCurrent) {
          setHasError(true);
        }
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    };

    fetchDetail();

    return () => {
      isCurrent = false;
    };
  }, [isOpen, expert?.slug]);

  // Drop the previous expert's episodes on close so reopening another card
  // never flashes the wrong list before its fetch resolves.
  useEffect(() => {
    if (!isOpen) {
      setDetail(null);
    }
  }, [isOpen]);

  if (!expert) return null;

  const episodes = detail?.episodes || [];
  const expertUrl = `/podcasts-masterclasses/experts/${expert.slug}`;

  // Prefer the fetched total, falling back to whatever the card supplied, so
  // the count is shown immediately and stays right once the detail lands.
  const episodeCount =
    detail?.episode_count ?? expert.episode_count ?? (detail ? 0 : null);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className='max-w-2xl border-white/10 bg-background text-white max-h-[85vh] overflow-y-auto'>
        <div className='flex items-start gap-4 pr-6'>
          <div className='relative overflow-hidden rounded-full shrink-0 size-16 bg-white/5'>
            <Image
              src={expert.photo_url || DummyLogo}
              layout='fill'
              objectFit='cover'
              alt={expert.name}
            />
          </div>
          <div className='flex-1 min-w-0'>
            <h2 className='text-xl font-bold leading-tight'>{expert.name}</h2>
            {expert.title && (
              <p className='mt-1 text-sm text-white/50'>{expert.title}</p>
            )}
            {episodeCount !== null && (
              <p className='mt-1 text-xs text-white/40'>
                {episodeCount} {episodeCount === 1 ? "episode" : "episodes"}
              </p>
            )}
          </div>
        </div>

        {expert.bio && (
          <p className='text-sm leading-relaxed text-white/70 line-clamp-3'>
            {expert.bio}
          </p>
        )}

        <div>
          <h3 className='text-sm font-semibold tracking-wide uppercase text-white/40'>
            Episodes
          </h3>

          {isLoading && (
            <div className='mt-3 space-y-2'>
              {[0, 1, 2].map((row) => (
                <div
                  key={row}
                  className='h-16 rounded-lg animate-pulse bg-white/5'
                />
              ))}
            </div>
          )}

          {!isLoading && hasError && (
            <p className='mt-3 text-sm text-white/50'>
              Could not load episodes.{" "}
              <Link href={expertUrl} className='underline hover:text-white'>
                Open the full profile
              </Link>{" "}
              instead.
            </p>
          )}

          {!isLoading && !hasError && episodes.length === 0 && (
            <p className='mt-3 text-sm text-white/50'>
              No published episodes yet.
            </p>
          )}

          {!isLoading && !hasError && episodes.length > 0 && (
            <ul className='mt-3 space-y-2'>
              {episodes.map((episode) => {
                const row = (
                  <>
                    <div className='relative w-24 overflow-hidden rounded shrink-0 aspect-video bg-white/5'>
                      <Image
                        src={
                          episode.thumbnail_url ||
                          episode.series?.thumbnail_url ||
                          DummyLogo
                        }
                        layout='fill'
                        objectFit='cover'
                        alt=''
                      />
                      {!episode.is_coming_soon && (
                        <span className='absolute inline-flex items-center justify-center -translate-x-1/2 -translate-y-1/2 rounded-full size-6 top-1/2 left-1/2 bg-white/90'>
                          <Play weight='fill' className='text-xs text-black' />
                        </span>
                      )}
                    </div>
                    <div className='flex-1 min-w-0'>
                      {episode.series?.title && (
                        <p className='text-[10px] font-semibold uppercase tracking-wide text-white/40 truncate'>
                          {episode.series.title}
                        </p>
                      )}
                      <p className='text-sm font-medium leading-snug line-clamp-2'>
                        {episode.title}
                      </p>
                      <p className='mt-0.5 text-xs text-white/40'>
                        {episode.is_coming_soon ? (
                          <span className='inline-flex items-center gap-1'>
                            <RocketLaunch weight='bold' />
                            Coming soon
                          </span>
                        ) : (
                          !!episode.seconds && secondsToMinutes(episode.seconds)
                        )}
                      </p>
                    </div>
                  </>
                );

                const rowClasses =
                  "flex items-center gap-3 p-2 rounded-lg border border-white/10 bg-white/[0.03]";

                return (
                  <li key={episode.id}>
                    {episode.is_coming_soon ? (
                      <div className={`${rowClasses} opacity-50`}>{row}</div>
                    ) : (
                      <Link
                        href={`/podcasts-masterclasses/${episode.slug}`}
                        className={`${rowClasses} transition-colors hover:bg-white/[0.08] hover:border-white/20`}
                      >
                        {row}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <Link
          href={expertUrl}
          className='inline-flex items-center gap-2 text-sm transition-colors text-white/60 hover:text-white'
        >
          <span>View full profile</span>
          <ArrowRight weight='bold' />
        </Link>
      </DialogContent>
    </Dialog>
  );
}
